import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { REVIEW_STATUS_COLOUR_MAP } from '../../../dashboard/insightsColourMap'
import { useBreadcrumbs } from '../../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { ReportCard, reportsBasePath } from '../../../reports/reportsConfig'

import { DatasetColumnConfig, Insights, toCsvFileName } from '@percent/domain/insights'
import { Flex, Heading, Icon, Text } from '@percent/lemonade'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'

type TasksReportProps = {
  report: ReportCard
}

const EXPORT_EXCLUDED_COLUMNS = ['dueBy']

/**
 * The grants Tasks report — every partner and organisation task across the partner's grant
 * programmes, with a task audience filter and column. Like the employee-donations report it
 * owns only what is specific to it: the review-status colour map, the dataset's column order,
 * and the composition of cards, charts and table inside its own `Insights.Domain`.
 *
 * Nothing here computes a number. The seven KPIs, the four breakdowns and the table
 * all name a backend slug; titles, subtitles and column headers come from the
 * response, so the report and the API cannot disagree about what a card means.
 */
export function TasksReport({ report }: Readonly<TasksReportProps>) {
  const { t } = useTranslation()
  const { insightsService } = useServices()

  const heading = t(report.titleKey)
  const subheading = t(report.descriptionKey)

  useBreadcrumbs([{ label: t('breadcrumb.reports'), path: reportsBasePath('grants') }, { label: heading }])

  const emptyState = useMemo(
    () => (
      <>
        <Icon name="empty" size={6} color="goodstack" />
        <Text size="small">{t('typography.dashboard.noDataToShow')}</Text>
      </>
    ),
    [t]
  )
  const errorLabel = t('typography.dashboard.failedToLoad')

  // Column order for the table. The response carries every column's label and type, so
  // this only orders them, pins the organisation as the row's identity, and drops the
  // id-only columns (taskId / organisationId / programId / taskTemplateName duplicates)
  // that belong in the CSV rather than on screen.
  const datasetColumns = useMemo<DatasetColumnConfig[]>(
    () => [
      { id: 'organisationName', isSticky: true, width: '220px' },
      { id: 'programName', width: '220px' },
      { id: 'taskTemplateName', width: '160px' },
      { id: 'audience' },
      { id: 'assignee', width: '260px' },
      { id: 'status' },
      { id: 'createdAt' },
      { id: 'resolvedAt' },
      { id: 'dueBy' }
    ],
    []
  )

  const filterLabels = {
    reset: t('typography.dashboard.filters.reset'),
    all: t('typography.dashboard.filters.all'),
    optionsError: t('typography.dashboard.filters.optionsLoadError'),
    dateRange: {
      // The platform's dateRange global is mapped to the task's created_at in this
      // domain, so it is labelled for what it filters here rather than generically.
      label: t('reports.tasks.filters.taskCreatedAt'),
      custom: t('typography.dashboard.filters.preset.custom'),
      apply: t('typography.dashboard.filters.apply'),
      clear: t('typography.dashboard.filters.clear')
    }
  }

  // Explicit order: the API returns platform globals before a domain's own filters,
  // which would put the date control ahead of Program name.
  const visibleFilters = [
    'programNames',
    'taskAudiences',
    'organisationCountries',
    'assigneeIds',
    'taskTemplateNames',
    'reviewStatuses',
    'dateRange'
  ]

  const exportLabels = {
    trigger: t('reports.export.trigger'),
    title: t('reports.export.title', { report: heading }),
    description: t('reports.export.description'),
    appliedFiltersHeading: t('reports.export.appliedFilters'),
    noFiltersApplied: t('reports.export.noFilters'),
    columnsLabel: t('reports.export.columns'),
    modeDefault: (count: number) => t('reports.export.mode.default', { count }),
    modeAll: (count: number) => t('reports.export.mode.all', { count }),
    modeCustom: t('reports.export.mode.custom'),
    includes: t('reports.export.includes'),
    zeroColumnsError: t('reports.export.zeroColumns'),
    cancel: t('button.cancel'),
    export: t('reports.export.confirm'),
    exportSuccess: (rowCount: number) => t('reports.export.success', { count: rowCount }),
    exportError: t('reports.export.error'),
    exportTooLarge: t('reports.export.tooLarge'),
    exportDateRangeTooLarge: t('reports.export.dateRangeTooLarge'),
    exportUnavailable: t('reports.export.unavailable'),
    close: t('button.close'),
    emptyTitle: t('reports.export.empty.title'),
    emptyFilteredDescription: t('reports.export.empty.filtered'),
    emptyNoDataDescription: t('reports.export.empty.noData')
  }

  return (
    <Insights.Domain domain={report.domain} service={insightsService} colourMap={REVIEW_STATUS_COLOUR_MAP}>
      <Flex
        direction="column"
        gap={24}
        w="100%"
        p={24}
        bg="white"
        style={{ borderRadius: 8 }}
        data-testid="report-detail"
      >
        <Flex direction="column" gap="xs">
          <Heading level="h2">{heading}</Heading>
          <Text size="small">{subheading}</Text>
        </Flex>

        {/* Own row so filter-bar width changes don't shift the heading, as in the
            employee-donations report (NEX-754). */}
        <Insights.Filters
          testId="report-filters"
          labels={filterLabels}
          visibleFilters={visibleFilters}
          // Left-aligned: a right-hung dropdown grows leftwards and pushes the bar's
          // leftmost filters off the card, which this report is wide enough to hit.
          dropdownAlign="start"
          dateRangeOptions={{ showAllTime: false }}
          singleSelectFilters={['taskAudiences']}
        />

        {/* The seven KPI tiles, in spec order. Only `task-count` carries a trend —
            every other tile is a function of state evaluated at request time (overdue,
            unassigned), so a period-over-period delta on it would read as movement
            that never happened. */}
        <Flex gap={16}>
          <Insights.Stat metric="task-count" errorLabel={errorLabel} testId="report-stat-task-count" />
          <Insights.Stat metric="completed-count" errorLabel={errorLabel} testId="report-stat-completed-count" />
          <Insights.Stat metric="overdue-count" errorLabel={errorLabel} testId="report-stat-overdue-count" />
          <Insights.Stat metric="unassigned-count" errorLabel={errorLabel} testId="report-stat-unassigned-count" />
          <Insights.Stat metric="task-template-count" errorLabel={errorLabel} testId="report-stat-template-count" />
          <Insights.Stat metric="reviewer-count" errorLabel={errorLabel} testId="report-stat-reviewer-count" />
          <Insights.Stat metric="candidate-count" errorLabel={errorLabel} testId="report-stat-candidate-count" />
        </Flex>

        {/* by-assignee is horizontal: the labels are reviewer emails, which never fit
            under a vertical axis. */}
        <Flex gap={16}>
          <Insights.BarChart
            metric="by-assignee"
            orientation="horizontal"
            yAxisWidth={220}
            emptyState={emptyState}
            errorLabel={errorLabel}
            testId="report-chart-by-assignee"
          />
          <Insights.BarChart
            metric="by-status"
            emptyState={emptyState}
            errorLabel={errorLabel}
            testId="report-chart-by-status"
          />
        </Flex>

        <Flex gap={16}>
          <Insights.BarChart
            metric="by-task-template"
            orientation="horizontal"
            yAxisWidth={220}
            emptyState={emptyState}
            errorLabel={errorLabel}
            testId="report-chart-by-task-template"
          />
          <Insights.BarChart
            metric="by-program"
            orientation="horizontal"
            yAxisWidth={220}
            emptyState={emptyState}
            errorLabel={errorLabel}
            testId="report-chart-by-program"
          />
        </Flex>

        <Insights.Dataset
          dataset="review-tasks"
          columns={datasetColumns}
          title={t('reports.tasks.table.title')}
          action={
            <Insights.Export
              dataset="review-tasks"
              defaultColumns={datasetColumns}
              excludeColumns={EXPORT_EXCLUDED_COLUMNS}
              fileName={toCsvFileName(heading)}
              labels={exportLabels}
              testId="report-export"
            />
          }
          pageSizeSelector={{ options: [10, 20, 50], label: t('typography.resultsOnPage') }}
          labels={{
            empty: t('reports.tasks.table.empty'),
            error: errorLabel,
            results: count => t('reports.tasks.table.results', { count })
          }}
          testId="report-dataset"
        />
      </Flex>
    </Insights.Domain>
  )
}
